"use client"

import { useState } from "react"

type Props = {
  sku: string
  label: string
  copyAria: string
  copiedLabel: string
}

/**
 * Inline SKU chip with copy-to-clipboard. B2B users search by part number,
 * so making it trivial to lift the value into another system matters.
 */
export default function SkuCopy({ sku, label, copyAria, copiedLabel }: Props) {
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(sku)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      // clipboard may be blocked on http:// or some webviews — silently noop
    }
  }

  return (
    <div className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-sm dark:bg-white/5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}:
      </span>
      <span className="font-mono font-medium text-slate-700 dark:text-slate-200">
        {sku}
      </span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copyAria}
        title={copied ? copiedLabel : copyAria}
        className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-white hover:text-brand-light dark:hover:bg-white/10 dark:hover:text-brand-dark"
      >
        {copied ? (
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4 text-emerald-500"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <rect x="7" y="7" width="9" height="9" rx="1.5" />
            <path d="M4 13V5a1 1 0 011-1h8" />
          </svg>
        )}
      </button>
    </div>
  )
}
